import React, { useMemo, useCallback, useState, useEffect, useRef } from "react";
import { MODALIDADES_PSS } from "../utils/HospitalizacionConstant";
import useEmpresa from "../../../hooks/useEmpresa";
import { useDebouncedSearchTerms } from "./useDebouncedSearchTerms";
import PrestadorService from "../../../services/prestador/PrestadorService";
import AuthService from "../../../services/auth/AuthService";
import {
  buildSearchParams,
  buildCoberturaOption,
  buildMedicoOption,
  buildEspecialidadOption,
  isMedicoCompatible,
  removeSearchTerm,
  updateSearchTerm,
} from "../utils/CoberturasMedicosUtils";

const useCoberturasMedicos = ({
  coberturasMedicos,
  onActualizarCobertura,
  onActualizarMedico,
  onActualizarModalidadPss,
  onActualizarEspecialidadMedico,
  onReiniciarCampos,
  obtenerMedicosDisponibles,
  getProcedimientosPssData,
  getProcedimientosPssLoading,
  getProcedimientosPssError,
  getMedicosData,
  getMedicosLoading,
  getMedicosError,
  manejarBusquedaProcedimiento,
  manejarBusquedaMedico,
  consultarTipoPssNoMedicoData,
  obtenerEspecialidadesMedicasData,
  obtenerEspecialidadesMedicasLoading,
  obtenerEspecialidadesMedicasError,
}) => {
  const { empresa } = useEmpresa();
  const [searchTerms, setSearchTerms] = useState({});
  const [searchTermsMedico, setSearchTermsMedico] = useState({});
  const [showCoberturaNoContratadaAlert, setShowCoberturaNoContratadaAlert] =
    useState(false);
  const [
    showPrestadorSinInformacionAlert,
    setShowPrestadorSinInformacionAlert,
  ] = useState(false);
  const medicoAutocompleteRefs = useRef({});
  const coberturaAutocompleteRefs = useRef({});
  const especialidadAutocompleteRefs = useRef({});
  const alertasMostradasRef = useRef(new Set());
  const loadingAnteriorRef = useRef(false);
  const limpiandoCoberturaRef = useRef(new Set());
  const limpiandoMedicoRef = useRef(new Set());

  const debouncedSearchTerms = useDebouncedSearchTerms(searchTerms);
  const debouncedSearchTermsMedico = useDebouncedSearchTerms(searchTermsMedico);

  const esTerminoValido = useCallback((term) => {
    return term && typeof term === "string" && term.trim();
  }, []);

  const limpiarAutocompleteMedico = useCallback((itemId, evitarClear = false) => {
    if (limpiandoMedicoRef.current.has(itemId)) {
      return;
    }
    
    limpiandoMedicoRef.current.add(itemId);
    setSearchTermsMedico((prev) => removeSearchTerm(prev, itemId));
    
    if (!evitarClear) {
      medicoAutocompleteRefs.current[itemId]?.current?.clear();
    }
    
    setTimeout(() => {
      limpiandoMedicoRef.current.delete(itemId);
    }, 0);
  }, []);

  const limpiarAutocompleteCobertura = useCallback((itemId, evitarClear = false) => {
    if (limpiandoCoberturaRef.current.has(itemId)) {
      return;
    }
    
    limpiandoCoberturaRef.current.add(itemId);
    setSearchTerms((prev) => removeSearchTerm(prev, itemId));
    
    if (!evitarClear) {
      coberturaAutocompleteRefs.current[itemId]?.current?.clear();
    }
    
    setTimeout(() => {
      limpiandoCoberturaRef.current.delete(itemId);
    }, 0);
  }, []);

  useEffect(() => {
    Object.keys(debouncedSearchTerms).forEach((itemId) => {
      const debouncedTerm = debouncedSearchTerms[itemId];
      if (!esTerminoValido(debouncedTerm) || !manejarBusquedaProcedimiento) {
        return;
      }

      const searchParams = buildSearchParams(debouncedTerm, true);
      if (searchParams) {
        manejarBusquedaProcedimiento(searchParams);
      }
    });
  }, [debouncedSearchTerms, manejarBusquedaProcedimiento, esTerminoValido]);

  useEffect(() => {
    Object.keys(debouncedSearchTermsMedico).forEach((itemId) => {
      const debouncedTerm = debouncedSearchTermsMedico[itemId];
      if (!esTerminoValido(debouncedTerm) || !manejarBusquedaMedico) {
        return;
      }

      const searchParams = buildSearchParams(debouncedTerm, false);
      if (searchParams) {
        manejarBusquedaMedico(searchParams);
      }
    });
  }, [debouncedSearchTermsMedico, manejarBusquedaMedico, esTerminoValido]);

  const especialidadesOptions = useMemo(() => {
    if (
      !obtenerEspecialidadesMedicasData?.items ||
      obtenerEspecialidadesMedicasLoading ||
      obtenerEspecialidadesMedicasError
    ) {
      return [];
    }
    return obtenerEspecialidadesMedicasData.items.map(buildEspecialidadOption);
  }, [
    obtenerEspecialidadesMedicasData?.items,
    obtenerEspecialidadesMedicasLoading,
    obtenerEspecialidadesMedicasError,
  ]);

  const coberturasOptions = useMemo(() => {
    if (
      !getProcedimientosPssData ||
      getProcedimientosPssLoading ||
      getProcedimientosPssError
    ) {
      return [];
    }
    return (getProcedimientosPssData.coberturas || []).map(
      buildCoberturaOption
    );
  }, [
    getProcedimientosPssData,
    getProcedimientosPssLoading,
    getProcedimientosPssError,
  ]);

  const verificarSinResultados = useCallback(
    (debouncedTerm, itemId) => {
      if (!esTerminoValido(debouncedTerm)) {
        return;
      }

      const alertKey = `${itemId}-${debouncedTerm}`;
      const tieneResultados = coberturasOptions.length > 0;
      const coberturaSeleccionada = coberturasOptions.find(
        (c) => c.label.toLowerCase().trim() === debouncedTerm.toLowerCase().trim()
      );

      if (tieneResultados) {
        alertasMostradasRef.current.delete(alertKey);
        return;
      }

      const sinResultados =
        getProcedimientosPssData &&
        Array.isArray(getProcedimientosPssData.coberturas) &&
        getProcedimientosPssData.coberturas.length === 0 &&
        !coberturaSeleccionada;

      if (sinResultados && !alertasMostradasRef.current.has(alertKey)) {
        alertasMostradasRef.current.add(alertKey);
        setShowCoberturaNoContratadaAlert(true);
      }
    },
    [coberturasOptions, getProcedimientosPssData, esTerminoValido]
  );

  useEffect(() => {
    const peticionCompletada =
      loadingAnteriorRef.current && !getProcedimientosPssLoading;

    loadingAnteriorRef.current = getProcedimientosPssLoading;

    if (!peticionCompletada) {
      return;
    }

    Object.keys(debouncedSearchTerms).forEach((itemId) => {
      const debouncedTerm = debouncedSearchTerms[itemId];
      verificarSinResultados(debouncedTerm, itemId);
    });
  }, [
    debouncedSearchTerms,
    getProcedimientosPssLoading,
    verificarSinResultados,
  ]);

  const medicosOptions = useMemo(() => {
    if (!getMedicosData || getMedicosLoading || getMedicosError) {
      return [];
    }
    return (getMedicosData.medicos || []).map(buildMedicoOption);
  }, [getMedicosData, getMedicosLoading, getMedicosError]);

  const handleActualizarCobertura = useCallback(
    (id, cobertura) => {
      const item = coberturasMedicos.find((item) => item.id === id);
      const medicoActual = item?.medico;

      if (medicoActual && cobertura) {
        const esCompatible = isMedicoCompatible(
          medicoActual,
          cobertura?.id,
          obtenerMedicosDisponibles
        );

        if (!esCompatible) {
          onActualizarMedico(id, null);
        }
      }

      const hayDuplicado = onActualizarCobertura(id, cobertura);
      if (hayDuplicado) {
        limpiarAutocompleteCobertura(id);
      }
    },
    [
      coberturasMedicos,
      onActualizarCobertura,
      onActualizarMedico,
      obtenerMedicosDisponibles,
      limpiarAutocompleteCobertura,
    ]
  );

  const limpiarTodosLosAutocompletes = useCallback((id) => {
    coberturaAutocompleteRefs.current[id]?.current?.clear();
    medicoAutocompleteRefs.current[id]?.current?.clear();
    especialidadAutocompleteRefs.current[id]?.current?.clear();
  }, []);

  const handleActualizarModalidadPss = useCallback(
    (id, nuevaModalidad) => {
      limpiarTodosLosAutocompletes(id);
      onReiniciarCampos(id);
      onActualizarModalidadPss(id, nuevaModalidad);
    },
    [onActualizarModalidadPss, onReiniciarCampos, limpiarTodosLosAutocompletes]
  );

  const handleCoberturaChange = useCallback(
    (itemId, value) => {
      if (limpiandoCoberturaRef.current.has(itemId)) {
        return;
      }

      if (!value || (typeof value === "string" && value.trim() === "")) {
        limpiarAutocompleteCobertura(itemId, true);
        handleActualizarCobertura(itemId, null);
        return;
      }

      setSearchTerms((prev) => updateSearchTerm(prev, itemId, value));

      const coberturaSeleccionada = coberturasOptions.find(
        (c) => c.label === value
      );

      if (coberturaSeleccionada) {
        handleActualizarCobertura(itemId, coberturaSeleccionada);
      }
    },
    [coberturasOptions, handleActualizarCobertura, limpiarAutocompleteCobertura]
  );

  const handleCoberturaSelect = useCallback(
    (itemId, option) => {
      limpiarAutocompleteCobertura(itemId);
      alertasMostradasRef.current.clear();
      handleActualizarCobertura(itemId, option);
    },
    [handleActualizarCobertura, limpiarAutocompleteCobertura]
  );

  const handleMedicoChange = useCallback(
    (itemId, value) => {
      if (limpiandoMedicoRef.current.has(itemId)) {
        return;
      }

      if (!value || (typeof value === "string" && value.trim() === "")) {
        limpiarAutocompleteMedico(itemId, true);
        onActualizarMedico(itemId, null);
        return;
      }

      setSearchTermsMedico((prev) => updateSearchTerm(prev, itemId, value));

      const medicoSeleccionado = medicosOptions.find((m) => m.label === value);

      if (medicoSeleccionado) {
        const hayDuplicado = onActualizarMedico(itemId, medicoSeleccionado);
        if (hayDuplicado) {
          limpiarAutocompleteMedico(itemId);
        }
      }
    },
    [medicosOptions, onActualizarMedico, limpiarAutocompleteMedico]
  );

  const validarMedicoContratado = useCallback(
    async (codigoMedico, cobertura, compania, nombreCobertura) => {
      const tokenResult = await AuthService.getTemporalTokenIsolated({
        username: codigoMedico,
        password: codigoMedico,
        compania: compania,
      });

      const accessToken = tokenResult?.data?.accessToken;
      if (!accessToken) {
        return { valido: false, sinInformacion: true };
      }

      const resultado =
        await PrestadorService.consultarProcedimientosPssWithTokenIsolated(
          {
            servicio: 2,
            TamanoPagina: 10,
            Pagina: 1,
            NombreCobertura: nombreCobertura || "",
            cobertura: cobertura,
          },
          accessToken
        );

      const sinProcedimientos = resultado?.data?.procedimientos?.length === 0;
      return { valido: !sinProcedimientos, sinInformacion: false };
    },
    []
  );

  const handleMedicoSelect = useCallback(
    async (itemId, option) => {
      setSearchTermsMedico((prev) => removeSearchTerm(prev, itemId));

      const item = coberturasMedicos.find((item) => item.id === itemId);
      const debeValidar =
        item?.modalidadPss === MODALIDADES_PSS.ARS &&
        item?.cobertura?.cobertura &&
        option?.codigo;

      if (debeValidar) {
        try {
          const codigoMedico = option.codigo.toString();
          const compania = empresa?.CODIGO;
          const nombreCobertura = item.cobertura?.nombreCobertura || "";
          const validacion = await validarMedicoContratado(
            codigoMedico,
            item.cobertura.cobertura,
            compania,
            nombreCobertura
          );

          if (!validacion.valido) {
            limpiarAutocompleteMedico(itemId);
            onActualizarMedico(itemId, null);
            if (validacion.sinInformacion) {
              setShowPrestadorSinInformacionAlert(true);
            } else {
              setShowCoberturaNoContratadaAlert(true);
            }
            return;
          }
        } catch (error) {
          limpiarAutocompleteMedico(itemId);
          onActualizarMedico(itemId, null);
          return;
        }
      }

      const hayDuplicado = onActualizarMedico(itemId, option);
      if (hayDuplicado) {
        limpiarAutocompleteMedico(itemId);
      }
    },
    [
      onActualizarMedico,
      coberturasMedicos,
      empresa,
      validarMedicoContratado,
      limpiarAutocompleteMedico,
    ]
  );

  const handleEspecialidadChange = useCallback(
    (itemId, value) => {
      if (!value || (typeof value === "string" && value.trim() === "")) {
        onActualizarEspecialidadMedico(itemId, null, null);
        return;
      }

      const especialidadSeleccionada = especialidadesOptions.find(
        (esp) => esp.value === value
      );

      if (especialidadSeleccionada) {
        onActualizarEspecialidadMedico(
          itemId,
          especialidadSeleccionada.especialidad,
          especialidadSeleccionada.codigoEspecialidad
        );
      } else {
        onActualizarEspecialidadMedico(itemId, null, null);
      }
    },
    [especialidadesOptions, onActualizarEspecialidadMedico]
  );

  const handleEspecialidadSelect = useCallback(
    (itemId, option) => {
      onActualizarEspecialidadMedico(
        itemId,
        option.especialidad,
        option.codigoEspecialidad
      );
    },
    [onActualizarEspecialidadMedico]
  );

  const getCoberturaAutocompleteRef = useCallback((itemId) => {
    if (!coberturaAutocompleteRefs.current[itemId]) {
      coberturaAutocompleteRefs.current[itemId] = React.createRef();
    }
    return coberturaAutocompleteRefs.current[itemId];
  }, []);

  const getMedicoAutocompleteRef = useCallback((itemId) => {
    if (!medicoAutocompleteRefs.current[itemId]) {
      medicoAutocompleteRefs.current[itemId] = React.createRef();
    }
    return medicoAutocompleteRefs.current[itemId];
  }, []);

  const getEspecialidadAutocompleteRef = useCallback((itemId) => {
    if (!especialidadAutocompleteRefs.current[itemId]) {
      especialidadAutocompleteRefs.current[itemId] = React.createRef();
    }
    return especialidadAutocompleteRefs.current[itemId];
  }, []);

  const handleCerrarCoberturaNoContratadaAlert = useCallback(() => {
    setShowCoberturaNoContratadaAlert(false);
    alertasMostradasRef.current.clear();
  }, []);

  return {
    especialidadesOptions,
    coberturasOptions,
    medicosOptions,
    showCoberturaNoContratadaAlert,
    showPrestadorSinInformacionAlert,
    handleActualizarModalidadPss,
    handleCoberturaChange,
    handleCoberturaSelect,
    handleMedicoChange,
    handleMedicoSelect,
    handleEspecialidadChange,
    handleEspecialidadSelect,
    getCoberturaAutocompleteRef,
    getMedicoAutocompleteRef,
    getEspecialidadAutocompleteRef,
    handleCerrarCoberturaNoContratadaAlert,
    setShowPrestadorSinInformacionAlert,
  };
};

export default useCoberturasMedicos;

